import { axios } from '/plugins/axios.client';
import { io } from 'socket.io-client';
import { BroadcastChannel, createLeaderElection } from 'broadcast-channel';

class SocketService {
  static instance = null; // Singleton instance
  socket = null;
  socketConnDetails = null;
  channel = null;
  elector = null;
  isLeader = false;
  messageHandlers = new Map();

  constructor() {
    if (SocketService.instance) {
      return SocketService.instance; // Always return existing instance
    }
    SocketService.instance = this;

    this.channel = new BroadcastChannel('socket-sync-channel');
    this.elector = createLeaderElection(this.channel);

    this._setupLeaderElection();
    this._setupChannelListeners();
  }

  async _setupLeaderElection() {
    try {
      await this.elector.awaitLeadership();
      this.isLeader = true;
      // Leader tab creates the actual socket connection
      await this.connect();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Leadership election error:', err);
    }
  }

  _setupChannelListeners() {
    // Listen for messages from other tabs
    this.channel.addEventListener('message', (msg) => {
      if (msg.type === 'socket-event') {
        // Broadcast socket events to local handlers
        this._emitToLocalHandlers(msg.event, msg.data);
      } else if (msg.type === 'emit-request' && this.isLeader) {
        // Leader receives emit requests from follower tabs
        if (this.socket?.connected) {
          this.socket.emit(msg.event, msg.data);
        }
      }
    });
  }

  _emitToLocalHandlers(event, data) {
    const handlers = this.messageHandlers.get(event);
    if (handlers) {
      handlers.forEach((handler) => handler(data));
    }
  }

  async getSocketEndpoint() {
    if (this.socketConnDetails) {
      return this.socketConnDetails;
    }

    try {
      this.socketConnDetails = await axios.$get('/api/pages/socket/client-endpoint');
      return this.socketConnDetails;
    } catch (err) {
      return null;
    }
  }

  async connect() {
    // Only leader tab should create real socket connection
    if (!this.isLeader) {
      return null;
    }

    // If already connected → return existing socket
    if (this.socket?.connected) return this.socket;

    const endpoint = await this.getSocketEndpoint();
    if (!endpoint) return null;

    const { clientEndpoint, path, socketKey } = endpoint;

    // create socket connection
    this.socket = io(clientEndpoint, {
      path,
      transports: ['websocket'],
      auth: { socketKey }
    });

    // eslint-disable-next-line no-console
    this.socket.on('disconnect', (reason) => console.warn('Socket disconnected due to ', reason));

    // eslint-disable-next-line no-console
    this.socket.on('connect_error', (error) => console.warn('Socket connection error', error));

    return this.socket;
  }

  /**
   * Listen to socket events (only works in leader tab)
   * @param {string} event - Event name
   * @param {Function} callback - Handler function
   */
  on(event, callback) {
    if (!this.isLeader) {
      // eslint-disable-next-line no-console
      console.warn(`Cannot listen to '${event}' - this is not the leader tab`);
      return () => {}; // Return empty cleanup function
    }

    if (this.socket) {
      this.socket.on(event, callback);
    }

    // Return cleanup function
    return () => this.off(event, callback);
  }

  /**
   * Emit event to socket (works from any tab)
   * @param {string} event - Event name
   * @param  {string} data - data to send
   */
  emit(event, data) {
    if (this.isLeader && this.socket?.connected) {
      // Leader tab emits directly
      this.socket.emit(event, data);
    } else if (this.channel) {
      // Follower tabs request leader to emit
      this.channel.postMessage({
        type: 'emit-request',
        event,
        data
      });
    }
  }

  /**
   * Remove event listener
   * @param {string} event - Event name
   * @param {Function} callback - Handler function
   */
  off(event, callback) {
    if (this.isLeader && this.socket) {
      this.socket.off(event, callback);
    }
  }

  getSocket() {
    return this.socket;
  }

  /**
   * Check if this tab is the leader
   */
  getIsLeader() {
    return this.isLeader;
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }

    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }

    if (this.elector) {
      this.elector.die();
      this.elector = null;
    }
    return null;
  }
}

// Export singleton instance
export const socketService = new SocketService();
